import { useState } from 'react';
import { Drawer } from 'antd';
import { Button } from 'components/ui/button';
import type { AdminSensorOverviewItem } from 'Views/SensorOverview/model';
import { sensorTypeLabel } from 'Views/SensorOverview/model';
import type {
  AlertRule,
  Capability,
  RuleDefinition,
  RulePreview,
  SaveRule,
  SensorScope,
} from './contracts';
import { alertDate, ErrorNotice, Field } from './shared';
import { isVersionConflict } from './transport';
import {
  commonCapabilities,
  conditionLabels,
  editableRule,
  faultLabels,
  initialCondition,
  metricLabels,
  newRule,
  ruleModeLabels,
  ruleSummary,
  validateRule,
} from './ruleHelpers';
import './ruleSettings.css';

type Props = {
  rule?: AlertRule;
  capabilities: Capability[];
  sensors: AdminSensorOverviewItem[];
  scope?: SensorScope;
  sensorType?: string;
  onSave: (command: SaveRule) => Promise<AlertRule>;
  onPreview: (rule: RuleDefinition, scope: SensorScope) => Promise<RulePreview>;
  onReload: () => Promise<AlertRule | undefined>;
  onClose: () => void;
};

function hasAdvancedSettings(rule: RuleDefinition) {
  return (
    rule.durationMinutes > 0 ||
    rule.recoveryMinutes > 0 ||
    rule.recovery === 'manual' ||
    rule.mode !== 'live' ||
    rule.actions.reminderMinutes !== null ||
    rule.actions.notifyOnRecovery ||
    (rule.condition.kind === 'numeric' && rule.condition.recoveryThreshold !== null)
  );
}

export default function RuleEditor({
  rule,
  capabilities,
  sensors,
  scope,
  sensorType,
  onSave,
  onPreview,
  onReload,
  onClose,
}: Props) {
  const [base, setBase] = useState(rule);
  const [draft, setDraft] = useState<RuleDefinition>(() =>
    rule ? editableRule(rule) : newRule(capabilities, scope, sensorType)
  );
  const [advancedOpen, setAdvancedOpen] = useState(() => hasAdvancedSettings(draft));
  const [error, setError] = useState<unknown>(null);
  const [conflict, setConflict] = useState(false);
  const [busy, setBusy] = useState<'save' | 'preview' | 'reload' | null>(null);
  const [preview, setPreview] = useState<{ key: string; value: RulePreview } | null>(null);
  const [previewSensor, setPreviewSensor] = useState(
    scope
      ? `${scope.orgId}:${scope.sensorId}`
      : rule?.scope.sensorId
      ? `${rule.scope.orgId}:${rule.scope.sensorId}`
      : ''
  );
  const fixedScope = Boolean(scope || base?.scope.sensorId);
  const scopeSensor = sensors.find(
    (sensor) => sensor.orgId === draft.scope.orgId && sensor.sensorId === draft.scope.sensorId
  );
  const scopeOrgName = sensors.find((sensor) => sensor.orgId === draft.scope.orgId)?.orgName;
  const sharedScope = Boolean(base && !base.scope.sensorId);
  const scopeSummary = draft.scope.sensorId
    ? `${scopeSensor?.sensorName || draft.scope.sensorId} · ${
        scopeSensor?.orgName || draft.scope.orgId
      }`
    : `${draft.scope.sensorTypes.map(sensorTypeLabel).join(', ')} · ${
        draft.scope.orgId ? scopeOrgName || draft.scope.orgId : 'alle kunder'
      }`;
  const support = commonCapabilities(capabilities, draft.scope.sensorTypes);
  const stale = Boolean(base && rule && rule.version > base.version) || conflict;
  const condition = draft.condition;
  const machineActivity = draft.scope.sensorTypes.includes('MACHINE_ACTIVITY');
  const availableSensors = sensors.filter(
    (sensor) =>
      draft.scope.sensorTypes.includes(sensor.type) &&
      (!draft.scope.orgId || sensor.orgId === draft.scope.orgId) &&
      (!draft.scope.sensorId || sensor.sensorId === draft.scope.sensorId)
  );
  const target = availableSensors.find(
    (sensor) => `${sensor.orgId}:${sensor.sensorId}` === previewSensor
  );
  const previewKey = JSON.stringify(draft) + previewSensor;
  const shownPreview = preview?.key === previewKey ? preview.value : null;
  const organisations = Array.from(
    new Map(sensors.map((sensor) => [sensor.orgId, sensor.orgName])).entries()
  );

  function set<K extends keyof RuleDefinition>(key: K, value: RuleDefinition[K]) {
    setDraft((current) => ({ ...current, [key]: value }));
    setError(null);
  }
  function setTypes(types: string[]) {
    setError(null);
    const nextSupport = commonCapabilities(capabilities, types);
    const activityNumeric = types.includes('MACHINE_ACTIVITY') && condition.kind === 'numeric';
    const nextCondition = activityNumeric
      ? initialCondition('numeric', nextSupport.metrics)
      : nextSupport.kinds.includes(condition.kind)
      ? condition
      : initialCondition(nextSupport.kinds[0] || 'no_data', nextSupport.metrics);
    setDraft((current) => ({
      ...current,
      scope: { ...current.scope, sensorTypes: types, sensorId: null },
      condition: nextCondition,
      ...(activityNumeric ? { durationMinutes: 0, recoveryMinutes: 0 } : {}),
      recovery: nextCondition.kind === 'message' ? 'manual' : current.recovery,
    }));
    setPreviewSensor('');
  }
  function close() {
    if (busy) return;
    const original = base ? editableRule(base) : newRule(capabilities, scope, sensorType);
    if (
      JSON.stringify(original) !== JSON.stringify(draft) &&
      !window.confirm('Kassér de ugemte ændringer til reglen?')
    )
      return;
    onClose();
  }
  async function save() {
    const invalid = validateRule(draft, capabilities);
    if (invalid) {
      setError(invalid);
      return;
    }
    if (stale || busy) return;
    setBusy('save');
    setError(null);
    try {
      await onSave({
        ...(base ? { id: base.id } : {}),
        expectedVersion: base?.version || 0,
        rule: draft,
      });
      onClose();
    } catch (cause) {
      setError(cause);
      setConflict(isVersionConflict(cause));
    } finally {
      setBusy(null);
    }
  }
  async function testRule() {
    const invalid = validateRule(draft, capabilities);
    if (invalid) {
      setError(invalid);
      return;
    }
    if (!target) {
      setError('Vælg en enhed til forhåndsvisningen.');
      return;
    }
    setBusy('preview');
    setError(null);
    try {
      setPreview({
        key: previewKey,
        value: await onPreview(draft, { orgId: target.orgId, sensorId: target.sensorId }),
      });
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(null);
    }
  }
  async function reload() {
    setBusy('reload');
    try {
      const latest = await onReload();
      if (!latest) throw new Error('Reglen kunne ikke genindlæses. Dit udkast er bevaret.');
      setBase(latest);
      setDraft(editableRule(latest));
      setAdvancedOpen(hasAdvancedSettings(latest));
      setConflict(false);
      setError(null);
      setPreview(null);
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(null);
    }
  }

  return (
    <Drawer
      open
      width={760}
      title={base ? `Rediger: ${base.title}` : 'Ny alarmregel'}
      onClose={close}
      destroyOnClose
    >
      <form
        className="unified-alerts ua-stack"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <p className="ua-muted">
          Redigér en eksisterende regel for at ændre dens adfærd. Nye regler kan tilføje flere
          alarmer for samme enhed.
        </p>
        <div
          className={sharedScope ? 'ua-notice ua-warning' : 'ua-notice'}
          aria-label="Reglens enheder"
        >
          <strong>
            {sharedScope ? 'Fælles regel – gælder alle matchende enheder' : 'Gælder for'}
          </strong>
          <p>{scopeSummary}</p>
          {sharedScope && (
            <p>
              Du ændrer den fælles regel for alle matchende enheder i dette område, også når du
              åbner den fra én enhed.
            </p>
          )}
        </div>
        {base?.isDefault && base.version === 0 && (
          <p className="ua-notice ua-warning">
            Standardreglen kan bruge tidligere gemte alarmindstillinger. At gemme reglen aktiverer
            ikke overførslen. Enhedens eksisterende alarmer fortsætter, indtil dens opsætning er
            overført og aktiveret. Målegrænser bevares, når du vælger “Enhedens gemte
            indstillinger”.
          </p>
        )}
        <fieldset disabled={Boolean(busy)} className="ua-stack">
          <Field label="Reglens navn">
            <input
              maxLength={120}
              value={draft.title}
              onChange={(e) => set('title', e.target.value)}
            />
          </Field>
          <div className="ua-grid">
            <Field label="Alvorlighed">
              <select
                value={draft.severity}
                onChange={(e) => set('severity', e.target.value as RuleDefinition['severity'])}
              >
                <option value="warning">Advarsel</option>
                <option value="critical">Kritisk</option>
              </select>
            </Field>
          </div>
          <label className="ua-check">
            <input
              type="checkbox"
              checked={draft.enabled}
              onChange={(e) => set('enabled', e.target.checked)}
            />
            Reglen er slået til
          </label>
          <hr className="ua-divider" />
          {!fixedScope && (
            <details className="ua-rule-options" open={!base}>
              <summary>Hvilke enheder gælder reglen for?</summary>
              <div className="ua-stack">
                <div className="ua-checkboxes" role="group" aria-label="Enhedstyper">
                  {capabilities.map((capability) => (
                    <label className="ua-check" key={capability.sensorType}>
                      <input
                        type="checkbox"
                        disabled={fixedScope}
                        checked={draft.scope.sensorTypes.includes(capability.sensorType)}
                        onChange={(e) =>
                          setTypes(
                            e.target.checked
                              ? [...draft.scope.sensorTypes, capability.sensorType]
                              : draft.scope.sensorTypes.filter(
                                  (type) => type !== capability.sensorType
                                )
                          )
                        }
                      />
                      {['geolocation_GY-NEO6MV2', 'GPS_GY_NEO', 'GPS_SIMTRACKING'].includes(
                        capability.sensorType
                      )
                        ? sensorTypeLabel(capability.sensorType)
                        : capability.title}
                    </label>
                  ))}
                </div>
                <div className="ua-grid">
                  <Field label="Organisation">
                    <select
                      disabled={fixedScope}
                      value={draft.scope.orgId || ''}
                      onChange={(e) => {
                        set('scope', {
                          ...draft.scope,
                          orgId: e.target.value || null,
                          sensorId: null,
                        });
                        setPreviewSensor('');
                      }}
                    >
                      <option value="">Alle organisationer</option>
                      {organisations.map(([id, name]) => (
                        <option key={id} value={id}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Enhed">
                    <select
                      disabled={fixedScope}
                      value={
                        draft.scope.sensorId ? `${draft.scope.orgId}:${draft.scope.sensorId}` : ''
                      }
                      onChange={(e) => {
                        const sensor = sensors.find(
                          (item) => `${item.orgId}:${item.sensorId}` === e.target.value
                        );
                        set('scope', {
                          ...draft.scope,
                          orgId: sensor?.orgId || draft.scope.orgId,
                          sensorId: sensor?.sensorId || null,
                        });
                        setPreviewSensor(e.target.value);
                      }}
                    >
                      <option value="">Alle matchende enheder</option>
                      {sensors
                        .filter(
                          (item) =>
                            draft.scope.sensorTypes.includes(item.type) &&
                            (!draft.scope.orgId || item.orgId === draft.scope.orgId)
                        )
                        .map((sensor) => (
                          <option
                            key={`${sensor.orgId}:${sensor.sensorId}`}
                            value={`${sensor.orgId}:${sensor.sensorId}`}
                          >
                            {sensor.sensorName} · {sensor.orgName}
                          </option>
                        ))}
                    </select>
                  </Field>
                </div>
              </div>
            </details>
          )}
          <h3>Hvornår skal alarmen oprettes?</h3>
          <div className="ua-capabilities" aria-label="Understøttede betingelser">
            {support.kinds.map((kind) => (
              <span className="ua-pill" key={kind}>
                {conditionLabels[kind]}
              </span>
            ))}
          </div>
          {!support.kinds.length && (
            <p className="ua-notice ua-warning">
              Ingen fælles alarmbetingelser er understøttet for de valgte enhedstyper.
            </p>
          )}
          <Field label="Betingelse">
            <select
              value={condition.kind}
              onChange={(e) => {
                const next = initialCondition(
                  e.target.value as typeof condition.kind,
                  support.metrics
                );
                set('condition', next);
                if (machineActivity && next.kind === 'numeric') {
                  set('durationMinutes', 0);
                  set('recoveryMinutes', 0);
                }
                if (next.kind === 'message') set('recovery', 'manual');
              }}
            >
              {!support.kinds.includes(condition.kind) && (
                <option disabled value={condition.kind}>
                  {conditionLabels[condition.kind]}
                </option>
              )}
              {support.kinds.map((kind) => (
                <option key={kind} value={kind}>
                  {conditionLabels[kind]}
                </option>
              ))}
            </select>
          </Field>
          {condition.kind === 'no_data' && (
            <Field
              label="Alarm efter antal minutter uden data"
              hint="1440 minutter er ét døgn. Enheden skal understøtte en forventet datarytme."
            >
              <input
                type="number"
                min={1}
                max={525600}
                value={Number.isNaN(condition.maxAgeMinutes) ? '' : condition.maxAgeMinutes}
                onChange={(e) =>
                  set('condition', { ...condition, maxAgeMinutes: e.target.valueAsNumber })
                }
              />
            </Field>
          )}
          {(condition.kind === 'numeric' || condition.kind === 'delta') && (
            <Field label="Måling">
              <select
                value={condition.metric}
                onChange={(e) => set('condition', { ...condition, metric: e.target.value, ...(machineActivity && condition.kind === 'numeric' ? { operator: e.target.value === 'inactive_days' ? 'above' as const : 'below' as const, comparison: 'strict' as const, recoveryComparison: 'inclusive' as const } : {}) })}
              >
                <option value="" disabled>
                  Vælg måling
                </option>
                {support.metrics.map((metric) => (
                  <option key={metric} value={metric}>
                    {metricLabels[metric] || metric}
                  </option>
                ))}
              </select>
            </Field>
          )}
          {condition.kind === 'numeric' && (
            <>
              <div className="ua-grid">
                <Field label="Alarmgrænse fra">
                  <select
                    disabled={machineActivity}
                    value={condition.thresholdSource}
                    onChange={(e) =>
                      set('condition', {
                        ...condition,
                        thresholdSource: e.target.value as 'rule' | 'device',
                      })
                    }
                  >
                    <option value="rule">Denne regel</option>
                    <option value="device">Enhedens gemte indstillinger</option>
                  </select>
                </Field>
                <Field label="Alarm når målingen er">
                  <select
                    disabled={machineActivity}
                    value={condition.operator}
                    onChange={(e) =>
                      set('condition', {
                        ...condition,
                        operator: e.target.value as 'above' | 'below',
                      })
                    }
                  >
                    <option value="below">{machineActivity ? 'Under grænsen' : 'På eller under grænsen'}</option>
                    <option value="above">{machineActivity ? 'Over grænsen' : 'På eller over grænsen'}</option>
                  </select>
                </Field>
              </div>
              {condition.thresholdSource === 'rule' ? (
                <div className="ua-grid">
                  <Field label="Alarmgrænse">
                    <input
                      type="number"
                      step="any"
                      value={Number.isNaN(condition.threshold) ? '' : condition.threshold}
                      onChange={(e) =>
                        set('condition', { ...condition, threshold: e.target.valueAsNumber })
                      }
                    />
                  </Field>
                </div>
              ) : (
                <p className="ua-notice">
                  Enheder uden en gemt grænse kan ikke vurderes. Forhåndsvis en konkret enhed
                  nedenfor.
                </p>
              )}
            </>
          )}
          {condition.kind === 'delta' && (
            <div className="ua-grid">
              <Field label="Mindste ændring">
                <input
                  type="number"
                  min={0.001}
                  step="any"
                  value={Number.isNaN(condition.threshold) ? '' : condition.threshold}
                  onChange={(e) =>
                    set('condition', { ...condition, threshold: e.target.valueAsNumber })
                  }
                />
              </Field>
              <Field label="Maks tid mellem målinger (minutter)">
                <input
                  type="number"
                  min={1}
                  max={10080}
                  value={Number.isNaN(condition.windowMinutes) ? '' : condition.windowMinutes}
                  onChange={(e) =>
                    set('condition', { ...condition, windowMinutes: e.target.valueAsNumber })
                  }
                />
              </Field>
            </div>
          )}
          {condition.kind === 'fault' && (
            <Field label="Fejl">
              <select
                value={condition.key}
                onChange={(e) => set('condition', { ...condition, key: e.target.value })}
              >
                <option value="*">{faultLabels['*']}</option>
                {support.faults.map((key) => (
                  <option key={key} value={key}>
                    {faultLabels[key] || key}
                  </option>
                ))}
              </select>
            </Field>
          )}
          {condition.kind === 'message' && (
            <Field
              label="SMS indeholder"
              hint="Brug * for alle beskeder. Gentagen tekst samles; afslutning kræver en vurdering."
            >
              <input
                maxLength={200}
                value={condition.contains}
                onChange={(e) => set('condition', { ...condition, contains: e.target.value })}
              />
            </Field>
          )}
          <hr className="ua-divider" />
          <h3>Hvordan skal der gives besked?</h3>
          <p className="ua-muted">
            Overførte regler bruger deres eksisterende modtagere. SMS kan kun bruge de numre, der er
            overført med opsætningen. Modtagerlisterne er skrivebeskyttede her; du kan ændre
            kanalerne. Gentagne observationer samles under den åbne hændelse.
          </p>
          <div className="ua-checkboxes">
            {(
              [
                ['notifyInApp', 'Notifikation i appen'],
                ['notifyEmail', 'E-mail'],
                ['notifySms', 'SMS til konfigurerede modtagere'],
              ] as const
            ).map(([key, label]) => (
              <label className="ua-check" key={key}>
                <input
                  type="checkbox"
                  checked={Boolean(draft.actions[key])}
                  disabled={key === 'notifySms' && !base?.id.startsWith('legacy_')}
                  onChange={(e) => {
                    if (key !== 'notifySms' || base?.id.startsWith('legacy_'))
                      set('actions', { ...draft.actions, [key]: e.target.checked });
                  }}
                />
                {label}
              </label>
            ))}
          </div>
          <p className="ua-muted">
            SMS kræver, at enhedens overførte opsætning er aktiveret. Eksisterende numre bevares;
            her tilføjes ingen nye SMS-modtagere.
          </p>
          <details
            className="ua-rule-options"
            open={advancedOpen}
            onToggle={(event) => setAdvancedOpen(event.currentTarget.open)}
          >
            <summary>
              Avanceret: forsinkelse, afslutning og påmindelser
              <span className="ua-rule-options-summary">
                {ruleModeLabels[draft.mode]}
                {' · '}
                {draft.durationMinutes > 0
                  ? `Alarm efter ${draft.durationMinutes} minutter`
                  : 'Alarm ved første registrering'}
                {' · '}
                {draft.recovery === 'manual'
                  ? 'Afsluttes manuelt'
                  : `Normal drift i ${draft.recoveryMinutes} minutter`}
                {' · '}
                {draft.actions.reminderMinutes
                  ? `Påmindelse hver ${draft.actions.reminderMinutes} minutter`
                  : 'Ingen gentagne påmindelser'}
              </span>
            </summary>
            <div className="ua-stack">
              <Field
                label="Reglens driftstilstand"
                hint="Testregler sender ikke driftsnotifikationer. Enhedens egen driftstilstand gælder også."
              >
                <select
                  value={draft.mode}
                  onChange={(e) => set('mode', e.target.value as RuleDefinition['mode'])}
                >
                  {Object.entries(ruleModeLabels).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </Field>
              <div className="ua-grid">
                <Field
                  label="Betingelsen skal vare (minutter)"
                  hint="0 opretter hændelsen ved første registrering."
                >
                  <input
                    type="number"
                    min={0}
                    max={525600}
                    disabled={machineActivity && condition.kind === 'numeric'}
                    value={Number.isNaN(draft.durationMinutes) ? '' : draft.durationMinutes}
                    onChange={(e) => set('durationMinutes', e.target.valueAsNumber)}
                  />
                </Field>
                <Field label="Normal drift skal vare (minutter)">
                  <input
                    type="number"
                    min={0}
                    max={525600}
                    disabled={machineActivity && condition.kind === 'numeric'}
                    value={Number.isNaN(draft.recoveryMinutes) ? '' : draft.recoveryMinutes}
                    onChange={(e) => set('recoveryMinutes', e.target.valueAsNumber)}
                  />
                </Field>
              </div>
              <Field label="Afslut hændelsen">
                <select
                  disabled={condition.kind === 'message'}
                  value={draft.recovery}
                  onChange={(e) => set('recovery', e.target.value as 'automatic' | 'manual')}
                >
                  <option value="automatic">Automatisk ved dokumenteret normal drift</option>
                  <option value="manual">Manuelt med årsag og note</option>
                </select>
              </Field>
              {condition.kind === 'numeric' && condition.thresholdSource === 'rule' && (
                <Field
                  label="Grænse for normal drift"
                  hint="Tomt felt bruger alarmgrænsen. Nul er en gyldig grænse."
                >
                  <input
                    type="number"
                    step="any"
                    value={condition.recoveryThreshold ?? ''}
                    onChange={(e) =>
                      set('condition', {
                        ...condition,
                        recoveryThreshold: e.target.value === '' ? null : e.target.valueAsNumber,
                      })
                    }
                  />
                </Field>
              )}
              <label className="ua-check">
                <input
                  type="checkbox"
                  checked={draft.actions.notifyOnRecovery}
                  onChange={(e) =>
                    set('actions', { ...draft.actions, notifyOnRecovery: e.target.checked })
                  }
                />
                Giv besked ved normal drift
              </label>
              <Field
                label="Påmindelse hver (minutter)"
                hint="Tomt felt sender ingen gentagne påmindelser. Mindst 15 minutter."
              >
                <input
                  type="number"
                  min={15}
                  max={525600}
                  value={draft.actions.reminderMinutes ?? ''}
                  onChange={(e) =>
                    set('actions', {
                      ...draft.actions,
                      reminderMinutes: e.target.value === '' ? null : e.target.valueAsNumber,
                    })
                  }
                />
              </Field>
              <Field label="Beskrivelse">
                <textarea
                  rows={2}
                  maxLength={1000}
                  value={draft.description}
                  onChange={(e) => set('description', e.target.value)}
                />
              </Field>
            </div>
          </details>
          <hr className="ua-divider" />
          <h3>Forhåndsvis med seneste data</h3>
          <p className="ua-muted">
            Viser betingelsen på den senest gemte måling. Opretter ingen hændelser og sender ingen
            beskeder.
          </p>
          <Field label="Enhed til forhåndsvisning">
            <select value={previewSensor} onChange={(e) => setPreviewSensor(e.target.value)}>
              <option value="">Vælg en enhed</option>
              {availableSensors.map((sensor) => (
                <option
                  key={`${sensor.orgId}:${sensor.sensorId}`}
                  value={`${sensor.orgId}:${sensor.sensorId}`}
                >
                  {sensor.sensorName} · {sensor.orgName}
                </option>
              ))}
            </select>
          </Field>
          <Button
            type="button"
            variant="outline"
            disabled={!target || Boolean(busy)}
            onClick={() => void testRule()}
          >
            Vis forventet resultat
          </Button>
        </fieldset>
        {shownPreview && (
          <div
            className={`ua-notice ${shownPreview.state === 'unknown' ? 'ua-warning' : ''}`}
            role="status"
          >
            <div>
              <strong>
                {shownPreview.state === 'firing'
                  ? 'Betingelsen er opfyldt'
                  : shownPreview.state === 'recovered'
                  ? 'Betingelsen er ikke opfyldt'
                  : 'Kan ikke vurderes'}
              </strong>
              <p>{shownPreview.explanation}</p>
              {shownPreview.assessments.map((assessment, index) => (
                <p key={index}>{assessment.explanation}</p>
              ))}
              <small>
                Data: {alertDate(shownPreview.heartbeat.lastDataAt)} · vurderet{' '}
                {alertDate(shownPreview.heartbeat.evaluatedAt)}
              </small>
            </div>
          </div>
        )}
        {stale && (
          <div className="ua-notice ua-warning" role="alert">
            Reglen er ændret siden åbningen. Dit udkast er bevaret. Hent den nye version for at
            redigere videre.
            <Button
              type="button"
              variant="outline"
              disabled={Boolean(busy)}
              onClick={() => void reload()}
            >
              Erstat udkast med seneste regel
            </Button>
          </div>
        )}
        {base && (
          <small className="ua-muted">
            Version {base.version} · opdateret {alertDate(base.updatedAt)} · {ruleSummary(base)}
          </small>
        )}
        <ErrorNotice error={error} />
        <div className="ua-actions">
          <Button type="submit" disabled={Boolean(busy) || stale}>
            {busy === 'save' ? 'Gemmer…' : 'Gem regel'}
          </Button>
          <Button type="button" variant="outline" disabled={Boolean(busy)} onClick={close}>
            Annuller
          </Button>
        </div>
      </form>
    </Drawer>
  );
}
